import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import AgencyVideoUploadModal from './AgencyVideoUploadModal';
import AgencyTargetingPanel from './AgencyTargetingPanel';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

const AGENCY_JWT_KEY = 'agency_jwt';
const getAgencyToken = () => localStorage.getItem(AGENCY_JWT_KEY) || '';

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding: 0;
  color: white;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  gap: 16px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const UploadBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 20px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  &:hover { background: #e0404f; }
`;

const UploadHint = styled.span`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.35);
  white-space: nowrap;
`;

const UploadGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 160px;
  max-width: 280px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: white;
  font-size: 0.82rem;
  padding: 6px 12px;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: rgba(255,255,255,0.25); }
  &:focus { border-color: rgba(255,71,87,0.5); }
`;

const SortBtn = styled.button`
  background: ${p => p.$active ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)'};
  color: ${p => p.$active ? 'white' : 'rgba(255,255,255,0.45)'};
  border: 1px solid ${p => p.$active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'};
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.09); color: white; }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  background: ${p => p.$active ? 'rgba(255,71,87,0.18)' : 'rgba(255,255,255,0.04)'};
  color: ${p => p.$active ? '#ff4757' : 'rgba(255,255,255,0.5)'};
  border: 1px solid ${p => p.$active ? 'rgba(255,71,87,0.4)' : 'rgba(255,255,255,0.1)'};
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 14px;
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: all 0.15s;
`;

const ROW_COLS = '28px 110px 1fr 160px 52px 105px 200px 36px';

const ListWrap = styled.div`
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: ${ROW_COLS};
  align-items: center;
  gap: 12px;
  padding: 9px 16px;
  background: rgba(255,255,255,0.03);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255,255,255,0.28);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${ROW_COLS};
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(255,255,255,0.02); }
`;

const ListTitle = styled.div`
  font-size: 0.88rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListDate = styled.div`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.3);
`;

const RowActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
`;

const RowActBtn = styled.button`
  background: ${p => p.$primary ? 'rgba(255,71,87,0.13)' : 'rgba(255,255,255,0.05)'};
  color: ${p => p.$primary ? '#ff7c89' : 'rgba(255,255,255,0.5)'};
  border: 1px solid ${p => p.$primary ? 'rgba(255,71,87,0.28)' : 'rgba(255,255,255,0.1)'};
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${p => p.$primary ? 'rgba(255,71,87,0.22)' : 'rgba(255,255,255,0.09)'};
    color: white;
  }
  &:disabled { opacity: 0.35; cursor: default; }
`;

const ExpandBtn = styled.button`
  width: 28px;
  height: 28px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: rgba(255,255,255,0.35);
  font-size: 0.65rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.08); color: white; }
`;

const ExpandPanel = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  padding: 16px 20px 20px;
  background: rgba(0,0,0,0.18);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

const ExpandVideo = styled.video`
  width: 100%;
  border-radius: 10px;
  background: #000;
  display: block;
`;

const ExpandInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.83rem;
  color: rgba(255,255,255,0.45);
  line-height: 1.55;
`;

const StatusPill = styled.span`
  flex-shrink: 0;
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${p => STATUS_STYLES[p.$s]?.bg || 'rgba(255,255,255,0.06)'};
  color: ${p => STATUS_STYLES[p.$s]?.fg || 'rgba(255,255,255,0.5)'};
  border: 1px solid ${p => STATUS_STYLES[p.$s]?.border || 'rgba(255,255,255,0.12)'};
`;

const RejectedBanner = styled.div`
  background: rgba(255,71,87,0.08);
  border: 1px solid rgba(255,71,87,0.2);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.76rem;
  color: rgba(255,100,120,0.9);
  margin-bottom: 10px;
`;

const TargetingTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
`;

const Tag = styled.span`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 0.7rem;
  color: rgba(255,255,255,0.4);
`;

const EditField = styled.div`margin-bottom: 10px;`;
const EditLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;
const EditInput = styled.input`
  width: 100%;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 7px;
  color: white;
  font-size: 0.85rem;
  padding: 7px 10px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: rgba(255,71,87,0.5); }
`;
const EditTextarea = styled.textarea`
  width: 100%;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 7px;
  color: white;
  font-size: 0.85rem;
  padding: 7px 10px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  &:focus { border-color: rgba(255,71,87,0.5); }
`;
const EditBtnRow = styled.div`display: flex; gap: 8px; margin-top: 10px;`;
const SaveBtn = styled.button`
  background: #ff4757; color: white; border: none; border-radius: 7px;
  font-size: 0.78rem; font-weight: 600; padding: 6px 16px; cursor: pointer;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { opacity: 0.4; cursor: default; }
`;
const CancelBtn = styled.button`
  background: transparent; color: rgba(255,255,255,0.45);
  border: 1px solid rgba(255,255,255,0.12); border-radius: 7px;
  font-size: 0.78rem; padding: 6px 14px; cursor: pointer;
  &:hover { color: white; border-color: rgba(255,255,255,0.3); }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 20px;
  color: rgba(255,255,255,0.3);
`;

const EmptyIcon = styled.div`font-size: 3rem; margin-bottom: 16px;`;
const EmptyTitle = styled.div`font-size: 1rem; font-weight: 600; color: rgba(255,255,255,0.5); margin-bottom: 8px;`;
const EmptyText = styled.div`font-size: 0.85rem; max-width: 280px; margin: 0 auto 24px;`;

const ErrorMsg = styled.div`
  background: rgba(255,71,87,0.1);
  border: 1px solid rgba(255,71,87,0.25);
  border-radius: 10px;
  color: #ff7c89;
  font-size: 0.85rem;
  padding: 14px 18px;
  text-align: center;
  margin-bottom: 20px;
`;

const LoadingRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const SkeletonCard = styled.div`
  flex: 1;
  min-width: 260px;
  height: 200px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%,100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255,71,87,0.07);
  border: 1px solid rgba(255,71,87,0.25);
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const BulkBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.65);
`;

const BulkBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BulkStatusSelect = styled.select`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 7px;
  color: white;
  font-size: 0.78rem;
  padding: 5px 10px;
  cursor: pointer;
  outline: none;
  option { background: #1a1a2e; }
`;

const BulkApplyBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 14px;
  cursor: pointer;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ClearSelBtn = styled.button`
  background: transparent;
  color: rgba(255,255,255,0.4);
  border: none;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover { color: white; }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #ff4757;
  flex-shrink: 0;
`;

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:          { bg: 'rgba(255,255,255,0.06)', fg: 'rgba(255,255,255,0.5)',  border: 'rgba(255,255,255,0.12)', label: 'Draft' },
  pending_review: { bg: 'rgba(255,200,0,0.1)',    fg: '#ffc800',               border: 'rgba(255,200,0,0.3)',    label: 'In Review' },
  approved:       { bg: 'rgba(46,204,113,0.1)',   fg: '#2ecc71',               border: 'rgba(46,204,113,0.3)',   label: 'Approved' },
  rejected:       { bg: 'rgba(255,71,87,0.1)',    fg: '#ff4757',               border: 'rgba(255,71,87,0.3)',    label: 'Rejected' },
  paused:         { bg: 'rgba(255,140,0,0.1)',    fg: '#ff8c00',               border: 'rgba(255,140,0,0.3)',    label: 'Paused' },
  archived:       { bg: 'rgba(100,100,100,0.1)',  fg: 'rgba(255,255,255,0.25)', border: 'rgba(255,255,255,0.07)', label: 'Archived' },
};

const STATUS_LABEL = s => STATUS_STYLES[s]?.label || s;

const filters = [
  { key: 'all',      label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'paused',   label: 'Paused' },
  { key: 'rejected', label: 'Rejected' },
];

// ─── Targeting summary helper ──────────────────────────────────────────────────

function targetingSummary(t) {
  if (!t) return [];
  const tags = [];
  if (t.target_genders?.length) tags.push(t.target_genders.join(', '));
  if (t.target_states?.length) {
    tags.push(t.target_states.length <= 3 ? t.target_states.join(', ') : `${t.target_states.length} states`);
  }
  if (t.target_birth_year_min || t.target_birth_year_max) {
    const age = y => new Date().getFullYear() - y;
    const min = t.target_birth_year_min ? age(t.target_birth_year_max || t.target_birth_year_min) : null;
    const max = t.target_birth_year_max ? age(t.target_birth_year_min || t.target_birth_year_max) : null;
    if (min && max) tags.push(`Age ${min}–${max}`);
    else if (min) tags.push(`Age ${min}+`);
  }
  if (t.target_employment_statuses?.length) {
    tags.push(t.target_employment_statuses.length <= 2 ? t.target_employment_statuses.join(', ') : `${t.target_employment_statuses.length} emp.`);
  }
  return tags;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

const AgencyVideoLibrary = ({ agencyProfile }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [patchingId, setPatchingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPatching, setBulkPatching] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('paused');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null); // 'name' | 'updated'
  const [sortDir, setSortDir] = useState('asc');

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/videos?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setVideos(data.videos || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const patchStatus = useCallback(async (videoId, status) => {
    setPatchingId(videoId);
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/videos/${videoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Failed to update status (${res.status})`);
      setVideos(prev => prev.map(v => v.video_id === videoId ? { ...v, status } : v));
    } catch (e) {
      setError(e.message);
    } finally {
      setPatchingId(null);
    }
  }, []);

  const startEdit = useCallback((v) => {
    setEditingId(v.video_id);
    setEditDraft({ title: v.title || '', description: v.description || '', url: v.url || '', points: v.points ?? 5, targeting: v.targeting || {} });
  }, []);

  const saveEdit = useCallback(async (videoId) => {
    setPatchingId(videoId);
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/videos/${videoId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: editDraft.title, description: editDraft.description, url: editDraft.url, points: editDraft.points, targeting: editDraft.targeting }),
      });
      if (!res.ok) throw new Error(`Failed to save (${res.status})`);
      setVideos(prev => prev.map(v => v.video_id === videoId
        ? { ...v, title: editDraft.title, description: editDraft.description, url: editDraft.url, points: editDraft.points, targeting: editDraft.targeting }
        : v));
      setEditingId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setPatchingId(null);
    }
  }, [editDraft]);

  const bulkPatchStatus = useCallback(async () => {
    if (!selectedIds.size) return;
    setBulkPatching(true);
    const token = getAgencyToken();
    const ids = [...selectedIds];
    const BATCH = 5;
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        await Promise.all(ids.slice(i, i + BATCH).map(id =>
          fetch(`${API_URL}/api/agency/videos/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status: bulkStatus }),
          })
        ));
      }
      setVideos(prev => prev.map(v => selectedIds.has(v.video_id) ? { ...v, status: bulkStatus } : v));
      setSelectedIds(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkPatching(false);
    }
  }, [selectedIds, bulkStatus]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const displayed = (() => {
    let list = filterStatus === 'all' ? videos : videos.filter(v => v.status === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(v => (v.title || '').toLowerCase().includes(q));
    }
    if (sortKey === 'name') {
      list = [...list].sort((a, b) => {
        const cmp = (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else if (sortKey === 'updated') {
      list = [...list].sort((a, b) => {
        const cmp = (a.updated_at || a.created_at || '').localeCompare(b.updated_at || b.created_at || '');
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  })();

  return (
    <PageWrap>
      <Toolbar>
        <PageTitle>My Ad Library</PageTitle>
        <UploadGroup>
          <UploadBtn onClick={() => setShowUploadModal(true)}>+ Upload new ad</UploadBtn>
          <UploadHint>mp4 / mov · max 500 MB</UploadHint>
        </UploadGroup>
      </Toolbar>

      <ControlsRow>
        <SearchInput
          placeholder="Search by name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <SortBtn $active={sortKey === 'name'} onClick={() => toggleSort('name')}>
          Name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
        </SortBtn>
        <SortBtn $active={sortKey === 'updated'} onClick={() => toggleSort('updated')}>
          Updated {sortKey === 'updated' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
        </SortBtn>
      </ControlsRow>

      <FilterRow>
        {filters.map(f => (
          <FilterBtn
            key={f.key}
            $active={filterStatus === f.key}
            onClick={() => setFilterStatus(f.key)}
          >
            {f.label}
            {f.key !== 'all' && (
              <span style={{ opacity: 0.6, marginLeft: 4 }}>
                ({videos.filter(v => v.status === f.key).length})
              </span>
            )}
          </FilterBtn>
        ))}
      </FilterRow>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {loading ? (
        <LoadingRow>
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </LoadingRow>
      ) : displayed.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🎬</EmptyIcon>
          <EmptyTitle>{filterStatus === 'all' ? 'No videos yet' : `No ${STATUS_LABEL(filterStatus).toLowerCase()} videos`}</EmptyTitle>
          <EmptyText>
            {filterStatus === 'all'
              ? 'Upload your first ad video to get started. Once published, it will be shown to matching subscribers.'
              : 'No videos match this filter.'}
          </EmptyText>
          {filterStatus === 'all' && (
            <UploadBtn onClick={() => setShowUploadModal(true)}>Upload first ad</UploadBtn>
          )}
        </EmptyState>
      ) : (
        <>
          {selectedIds.size > 0 && (
            <BulkBar>
              <BulkBarLeft>
                <span>{selectedIds.size} video{selectedIds.size !== 1 ? 's' : ''} selected</span>
                <ClearSelBtn onClick={() => setSelectedIds(new Set())}>Clear selection</ClearSelBtn>
              </BulkBarLeft>
              <BulkBarRight>
                <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>Set status to:</span>
                <BulkStatusSelect value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
                  <option value="approved">Approved</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                  <option value="rejected">Rejected</option>
                </BulkStatusSelect>
                <BulkApplyBtn disabled={bulkPatching} onClick={bulkPatchStatus}>
                  {bulkPatching ? 'Applying…' : 'Apply'}
                </BulkApplyBtn>
              </BulkBarRight>
            </BulkBar>
          )}
          <ListWrap>
          <ListHeader>
            <Checkbox
              checked={displayed.length > 0 && displayed.every(v => selectedIds.has(v.video_id))}
              ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !displayed.every(v => selectedIds.has(v.video_id)); }}
              onChange={e => {
                if (e.target.checked) setSelectedIds(new Set(displayed.map(v => v.video_id)));
                else setSelectedIds(new Set());
              }}
            />
            <div>Status</div>
            <div>Title</div>
            <div>Advertiser URL</div>
            <div>Pts</div>
            <div>Added</div>
            <div>Actions</div>
            <div />
          </ListHeader>
          {displayed.map(v => {
            const isExpanded = expandedId === v.video_id;
            const tags = targetingSummary(v.targeting);
            return (
              <React.Fragment key={v.video_id}>
                <ListRow>
                  <Checkbox
                    checked={selectedIds.has(v.video_id)}
                    onChange={e => {
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(v.video_id); else next.delete(v.video_id);
                        return next;
                      });
                    }}
                  />
                  <StatusPill $s={v.status}>{STATUS_LABEL(v.status)}</StatusPill>
                  <ListTitle title={v.title}>{v.title}</ListTitle>
                  <ListDate style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.url
                      ? <a href={v.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2ecc71', textDecoration: 'none', fontSize: '0.75rem' }} title={v.url}>
                          {(() => { try { return new URL(v.url).hostname.replace(/^www\./, ''); } catch { return v.url; } })()}
                        </a>
                      : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                    }
                  </ListDate>
                  <ListDate style={{ textAlign: 'center' }}>{v.points ?? 5}</ListDate>
                  <ListDate>{fmtDate(v.created_at)}</ListDate>
                  <RowActions>
                    {v.status === 'draft' && (
                      <RowActBtn $primary disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'approved')}>Publish</RowActBtn>
                    )}
                    {v.status === 'pending_review' && (
                      <>
                        <RowActBtn $primary disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'approved')}>Publish</RowActBtn>
                        <RowActBtn disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'draft')}>Recall</RowActBtn>
                      </>
                    )}
                    {v.status === 'rejected' && (
                      <RowActBtn $primary disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'approved')}>Publish</RowActBtn>
                    )}
                    {v.status === 'approved' && (
                      <RowActBtn disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'paused')}>Pause</RowActBtn>
                    )}
                    {v.status === 'paused' && (
                      <RowActBtn $primary disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'approved')}>Resume</RowActBtn>
                    )}
                    {v.status !== 'archived' && (
                      <RowActBtn disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'archived')}>Archive</RowActBtn>
                    )}
                    {v.status === 'archived' && (
                      <RowActBtn $primary disabled={patchingId === v.video_id} onClick={() => patchStatus(v.video_id, 'draft')}>Unarchive</RowActBtn>
                    )}
                  </RowActions>
                  <ExpandBtn onClick={() => setExpandedId(isExpanded ? null : v.video_id)}>
                    {isExpanded ? '▲' : '▼'}
                  </ExpandBtn>
                </ListRow>
                {isExpanded && (
                  <ExpandPanel>
                    <div>
                      {v.blob_url
                        ? <ExpandVideo src={v.blob_url} controls preload="metadata" />
                        : <div style={{ background: '#0a0a14', borderRadius: 10, aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.15)', fontSize: '2rem' }}>▶</div>
                      }
                    </div>
                    <ExpandInfo>
                      {editingId === v.video_id ? (
                        <>
                          <EditField>
                            <EditLabel>Title</EditLabel>
                            <EditInput
                              value={editDraft.title}
                              onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                              maxLength={120}
                            />
                          </EditField>
                          <EditField>
                            <EditLabel>Description</EditLabel>
                            <EditTextarea
                              value={editDraft.description}
                              onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                              maxLength={500}
                            />
                          </EditField>
                          <EditField>
                            <EditLabel>Advertiser website URL</EditLabel>
                            <EditInput
                              type="url"
                              value={editDraft.url}
                              onChange={e => setEditDraft(d => ({ ...d, url: e.target.value }))}
                              placeholder="https://example.com"
                              maxLength={500}
                            />
                          </EditField>
                          <EditField>
                            <EditLabel>Points per view</EditLabel>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={editDraft.points ?? 5}
                              onChange={e => setEditDraft(d => ({ ...d, points: Math.max(1, Math.min(100, Number(e.target.value) || 5)) }))}
                              style={{ width: 80, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', padding: '8px 12px', fontSize: '0.95rem' }}
                            />
                          </EditField>
                          <EditField>
                            <EditLabel>Targeting</EditLabel>
                            <AgencyTargetingPanel
                              value={editDraft.targeting}
                              onChange={targeting => setEditDraft(d => ({ ...d, targeting }))}
                            />
                          </EditField>
                          <EditBtnRow>
                            <SaveBtn disabled={patchingId === v.video_id} onClick={() => saveEdit(v.video_id)}>
                              {patchingId === v.video_id ? 'Saving…' : 'Save changes'}
                            </SaveBtn>
                            <CancelBtn onClick={() => setEditingId(null)}>Cancel</CancelBtn>
                          </EditBtnRow>
                        </>
                      ) : (
                        <>
                          {v.description && <div>{v.description}</div>}
                          {v.url && (
                            <div style={{ marginTop: 6 }}>
                              <a
                                href={v.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#65C2D5', fontSize: '0.82rem', wordBreak: 'break-all' }}
                              >
                                🔗 {v.url}
                              </a>
                            </div>
                          )}
                          {v.status === 'rejected' && v.rejection_reason && (
                            <RejectedBanner>Rejected: {v.rejection_reason}</RejectedBanner>
                          )}
                          {tags.length > 0 && (
                            <TargetingTags>
                              {tags.map((t, i) => <Tag key={i}>{t}</Tag>)}
                            </TargetingTags>
                          )}
                          {!v.description && !v.rejection_reason && tags.length === 0 && (
                            <span style={{ color: 'rgba(255,255,255,0.2)' }}>No additional details</span>
                          )}
                          <div>
                            <CancelBtn style={{ marginTop: 4 }} onClick={() => startEdit(v)}>Edit details &amp; targeting</CancelBtn>
                          </div>
                        </>
                      )}
                    </ExpandInfo>
                  </ExpandPanel>
                )}
              </React.Fragment>
            );
          })}
          </ListWrap>
        </>
      )}

      {showUploadModal && (
        <AgencyVideoUploadModal
          onClose={() => setShowUploadModal(false)}
          onCreated={() => { setShowUploadModal(false); fetchVideos(); }}
          defaultTargeting={agencyProfile?.default_targeting}
        />
      )}
    </PageWrap>
  );
};

export default AgencyVideoLibrary;
